import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, buildUrl } from "@shared/routes";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";

export function useFavorites() {
  return useQuery({
    queryKey: [api.favorites.list.path],
    queryFn: async () => {
      const res = await fetch(api.favorites.list.path, { credentials: "include" });
      if (res.status === 401) return [];
      if (!res.ok) throw new Error("Failed to fetch favorites");
      return res.json();
    },
  });
}

export function useCheckFavorite(listingId: number, enabled = true) {
  return useQuery({
    queryKey: [api.favorites.check.path, listingId],
    queryFn: async () => {
      const url = buildUrl(api.favorites.check.path, { listingId });
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return { isFavorite: false };
      return res.json();
    },
    enabled,
  });
}

export function useAddFavorite() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: async (listingId: number) => {
      const res = await fetch(api.favorites.add.path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listingId }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to add favorite");
      return res.json();
    },
    onMutate: async (listingId) => {
      // Optimistic update - immediately show as favorite
      await queryClient.cancelQueries({ queryKey: [api.favorites.check.path, listingId] });
      const previousData = queryClient.getQueryData([api.favorites.check.path, listingId]);
      queryClient.setQueryData([api.favorites.check.path, listingId], { isFavorite: true });
      return { previousData };
    },
    onSuccess: (_, listingId) => {
      queryClient.invalidateQueries({ queryKey: [api.favorites.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.favorites.check.path, listingId] });
      toast({ title: t("toast.addedToFavorites") });
    },
    onError: (_, listingId, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData([api.favorites.check.path, listingId], context.previousData);
      }
      toast({ title: t("toast.favoriteLoginRequired"), variant: "destructive" });
    }
  });
}

export function useRemoveFavorite() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: async (listingId: number) => {
      const url = buildUrl(api.favorites.remove.path, { listingId });
      const res = await fetch(url, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to remove favorite");
    },
    onMutate: async (listingId) => {
      // Optimistic update - immediately remove from favorites
      await queryClient.cancelQueries({ queryKey: [api.favorites.check.path, listingId] });
      const previousData = queryClient.getQueryData([api.favorites.check.path, listingId]);
      queryClient.setQueryData([api.favorites.check.path, listingId], { isFavorite: false });
      return { previousData };
    },
    onSuccess: (_, listingId) => {
      queryClient.invalidateQueries({ queryKey: [api.favorites.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.favorites.check.path, listingId] });
      toast({ title: t("toast.removedFromFavorites") });
    },
    onError: (_, listingId, context) => {
      // Rollback on error
      if (context?.previousData) {
        queryClient.setQueryData([api.favorites.check.path, listingId], context.previousData);
      }
    },
  });
}
